import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { REVIEW } from "@/constants/appIds";
import { formatDateTimeJakarta, formatRupiah, STATUS_META, OCR_META } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ReceiptThumb } from "@/components/ReceiptImage";
import { AlertTriangle, ChevronRight, Inbox } from "lucide-react";

export default function ReviewList() {
  const nav = useNavigate();
  const { data = [], isLoading } = useQuery({
    queryKey: ["review-list"],
    queryFn: async () => (await api.get("/receipts")).data,
    refetchInterval: 6000,
  });

  const pending = data.filter((r) => ["pending_review", "draft"].includes(r.status));
  const others = data.filter((r) => !["pending_review", "draft"].includes(r.status));

  const Row = (r) => {
    const variance = r.totals?.variance;
    return (
      <button key={r.id} data-testid={REVIEW.card} onClick={() => nav(`/review/${r.id}`)}
        className="w-full text-left">
        <Card className="p-3 flex items-center gap-3 hover:border-primary/40 transition-colors">
          <div className="w-14 h-14 rounded-lg overflow-hidden bg-secondary shrink-0">
            {r.photos?.[0] && <ReceiptThumb path={r.photos[0].storage_path} className="w-full h-full object-cover" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium truncate">{r.corrected?.supplier || "Supplier belum terbaca"}</p>
              <Badge variant="outline" className={`text-[10px] ${STATUS_META[r.status]?.cls}`}>{STATUS_META[r.status]?.label}</Badge>
              <Badge variant="outline" className={`text-[10px] ${OCR_META[r.ocr_status]?.cls}`}>{OCR_META[r.ocr_status]?.label}</Badge>
              {variance !== null && variance !== 0 && (
                <Badge variant="outline" className="text-[10px] bg-orange-100 text-orange-700 border-orange-300">
                  <AlertTriangle className="w-3 h-3 mr-1" /> Selisih {formatRupiah(Math.abs(variance))}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {r.outlet_name} · {formatDateTimeJakarta(r.uploaded_at)} · {formatRupiah(r.totals?.computed_total)}
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
        </Card>
      </button>
    );
  };

  return (
    <div className="space-y-6 nh-fade" data-testid={REVIEW.list}>
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Pemeriksaan Admin</h1>
        <p className="text-sm text-muted-foreground">Periksa hasil pembacaan AI, perbaiki, lalu setujui untuk masuk ke Daily Report.</p>
      </div>

      {isLoading ? (
        <div className="space-y-3">{[0,1,2].map(i => <div key={i} className="h-20 rounded-xl bg-secondary animate-pulse" />)}</div>
      ) : (
        <>
          <section>
            <h2 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
              Perlu Diperiksa ({pending.length})
            </h2>
            <div className="space-y-2">
              {pending.length === 0 && (
                <Card className="p-8 text-center text-muted-foreground">
                  <Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Tidak ada nota menunggu.</p>
                </Card>
              )}
              {pending.map(Row)}
            </div>
          </section>

          {others.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">Sudah Diproses</h2>
              <div className="space-y-2">{others.map(Row)}</div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
